import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthContext.jsx';
import { api } from '../api/client.js';
import { fmt } from './ui.jsx';

const ENV_LABEL = import.meta.env.VITE_ENV_LABEL || 'Local';

export default function AppLayout({ theme, onToggleTheme }) {
  const { user, signOut, can } = useAuth();
  const navigate = useNavigate();

  // Pending-review count drives the sidebar badge; refreshed on a slow poll so
  // reviewers see new work without reloading.
  const { data: overview } = useQuery({
    queryKey: ['overview'],
    queryFn: () => api.overview().then((r) => r.data),
    refetchInterval: 60_000,
  });

  const pending = overview?.mappings?.pending ?? 0;
  const ratingFail = overview?.mappings?.rating?.fail ?? 0;

  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="brand">
          <div className="brand-name">MMV Mapper</div>
          <div className="brand-sub">GIIB vehicle mapping</div>
        </div>

        <nav className="nav">
          <div className="nav-group-label">Overview</div>
          <NavLink to="/dashboard" className="nav-item">Dashboard</NavLink>
          <NavLink to="/analytics" className="nav-item">
            Analytics
            {ratingFail > 0 && <span className="nav-count">{fmt(ratingFail)}</span>}
          </NavLink>

          <div className="nav-group-label">Mapping</div>
          <NavLink to="/mappings" className="nav-item">
            Review queue
            {pending > 0 && <span className="nav-count">{fmt(pending)}</span>}
          </NavLink>
          <NavLink to="/upload" className="nav-item">Upload master</NavLink>
          <NavLink to="/imports" className="nav-item">Import history</NavLink>

          <div className="nav-group-label">Reference data</div>
          <NavLink to="/registry" className="nav-item">Key registry</NavLink>
          <NavLink to="/vehicles" className="nav-item">GIIB master</NavLink>
          <NavLink to="/insurers" className="nav-item">Insurers</NavLink>

          {can('admin') && (
            <>
              <div className="nav-group-label">Administration</div>
              <NavLink to="/admin" className="nav-item">Settings and audit</NavLink>
            </>
          )}
        </nav>

        <div className="sidebar-footer">
          <div className="user-line">
            <div className="avatar" aria-hidden="true">
              {(user?.name || '?').split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase()}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.name}</div>
              <div className="prov">{user?.role}</div>
            </div>
          </div>
          <div className="btn-row">
            <button className="btn btn-sm btn-ghost" onClick={onToggleTheme}>
              {theme === 'dark' ? 'Light mode' : 'Dark mode'}
            </button>
            <button
              className="btn btn-sm btn-ghost"
              onClick={() => { signOut(); navigate('/login', { replace: true }); }}
            >
              Sign out
            </button>
          </div>
        </div>
      </aside>

      <div className="main">
        <header className="topbar">
          <button className="btn btn-sm btn-ghost" onClick={() => navigate(-1)} aria-label="Go back">Back</button>
          <div className="spacer" />
          <span className="badge badge-neutral">{ENV_LABEL}</span>
        </header>
        <Outlet />
      </div>
    </div>
  );
}
